/**
 * DWG Clean Service
 *
 * 맵에디터 도면 가져오기에서 DWG 를 쓰려면, 사내 윈도우 PC 의 AutoCAD 로 맵에 필요 없는
 * 레이어를 지운 DXF 를 먼저 만든다(백엔드 /v0.1/dwg-clean/*).
 */

import axios from 'axios'
import { createAuthenticatedClient } from './api/apiClient'
import type { FileInfo } from '../models/FileStorage'

// AutoCAD 로 도면을 열고 레이어를 지우는 데 수십 초~수 분 — DXF 변환과 같은 상한.
const api = createAuthenticatedClient({ timeout: 1200000 })

export interface DwgLayer {
  name: string
  count: number // 개체 수(모형 공간 + 블록 안)
  sampleText?: string // 그 레이어 문자 예시 (IP 라벨 확인용)
  keep: boolean // 키워드·AI·모양 추천
  locked: boolean // 항상 남길 레이어 (0, Defpoints, 외부참조, CCTV IP 라벨)
  ipLabel?: boolean // CCTV IP 라벨 레이어 — 맵에서 CCTV 와 IP 를 매핑할 때 쓴다
  aiKind?: string // AI 가 남기라고 고른 종류 (parking|cctv|ipLabel|bell|light|pillar|outline)
  aiReason?: string // AI 가 고른 근거 한 줄
  shapeKind?: string // 모양(심볼 지문·주차면/기둥 크기 사각형)으로 고른 종류 (parking|cctv|bell|light|pillar)
  shapeReason?: string // 모양으로 고른 근거 한 줄 — 이름이 키워드에 안 걸려도 잡는다
}

/** 도면에서 센 맵 객체 한 종류 (블록 하나 = 객체 하나로 센 추정치). */
export interface DwgCount {
  kind: string // cctv | pillar | parking | bell | light | ipLabel
  label: string
  total: number
  items: { name: string; count: number }[] // 어디서 셌는지 ("레이어 · 블록") — 많은 순
}

export interface DwgLayersData {
  layers: DwgLayer[]
  counts: DwgCount[]
  ai: string // AI 추천 결과 한 줄 (실패·미설정이면 그 사유)
}

export interface DwgCleanResult {
  file: FileInfo
  deletedCount: number
  failedLayers: string[]
}

interface ApiEnvelope<T> {
  success: boolean
  message: string
  data?: T
}

export function isDwgFilename(name: string): boolean {
  return name.toLowerCase().endsWith('.dwg')
}

async function post<T>(url: string, body: object, fallback: string): Promise<T> {
  let res
  try {
    res = await api.post<ApiEnvelope<T>>(url, body)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      throw new Error(error.response.data.message)
    }
    throw new Error(fallback)
  }
  if (!res.data.success || !res.data.data) {
    throw new Error(res.data.message || fallback)
  }
  return res.data.data
}

export class DwgCleanService {
  static async listLayers(projectId: number, dwgFilename: string): Promise<DwgLayersData> {
    const data = await post<DwgLayersData>('/v0.1/dwg-clean/layers', { projectId, dwgFilename }, 'DWG 레이어 목록을 읽지 못했습니다')
    return { layers: data.layers, counts: data.counts ?? [], ai: data.ai ?? '' }
  }

  static clean(projectId: number, dwgFilename: string, deleteLayers: string[]): Promise<DwgCleanResult> {
    return post<DwgCleanResult>('/v0.1/dwg-clean/clean', { projectId, dwgFilename, deleteLayers }, 'DWG 정리에 실패했습니다')
  }
}
